import { bucketOfTask, resolveIslandState, type IslandColor } from './agent-task-buckets';
import { useIslandQuickView } from './use-island-quickview';

export interface IslandState {
  /** green = resting (agents · skills), blue = agents at work, orange = waiting on you. */
  color: IslandColor;
  /** Things with a live run RIGHT NOW: tasks with `runInFlight === true` across the loaded account-wide
   *  list, PLUS the Maestro's own in-flight non-task actions (`maestroActions`). */
  workingCount: number;
  /** DISTINCT agents behind those — the blue pill's count. Missing-agent rows and maestro actions all
   *  fold into ONE Maestro bucket. Zero while the list is loading or when nothing carries a computed
   *  run state, which is exactly the "never blue from ignorance" rule. */
  workingAgents: number;
  /** The in-progress BUCKET count (the old blue number). No longer the colour's driver — a paused or
   *  queued task buckets here with nothing running — but the agent-started announcement's baseline and
   *  the island click BI still read it. */
  inMotionCount: number;
  waitingCount: number;
}

/**
 * The top-bar island's resolved state. The island is a GLOBAL, account-wide affordance — it must never
 * count only what happens to fall inside the roster's workspaces — so both its numbers now come off the
 * SAME account-wide `/api/tasks` list the Tasks screen and its own quick-view render from
 * (`useIslandQuickView`'s `quickView.data.sorted`), rather than a per-workspace fan-out
 * (`/api/workspace-tasks?workspaceId=` summed over the roster).
 *
 * That fan-out used to undercount here in two ways the tab was never subject to: it structurally cannot
 * see an inbound outreach row (they carry no `workspaceId` by design — see `/api/tasks`'s
 * `receivedOutreachRows`), and it drops any task whose `workspaceId` is empty or sits outside the one-level
 * child-project rollup. Reading the one account-wide list the tab already reads means the pill and the tab
 * it opens can only disagree for the moment a refetch is in flight, never structurally.
 *
 * BLUE = agents whose loop is EXECUTING right now — computed client-side from the list's per-row
 * `runInFlight` verdict (web's equivalent of the aggregate's `totalWorking`/`workingAgents`; the phones
 * read those fields off the bridge). **Tri-state discipline**: a row with no `runInFlight` was never
 * computed and counts nothing, so an older server or a not-yet-loaded list falls through to orange/green
 * rather than asserting either way. Orange = waiting (ACTION_NEEDED), including outreach rows filed
 * ACTION_NEEDED by `outreachRowStatus`; a failed task is finished, not waiting on anyone, and lives on the
 * Failed tab (see `islandCountsFromRows`'s comment on why `failed` is excluded). While the read is still
 * in flight the counts are zero and the island rests green, exactly the pre-data state.
 *
 * `waitingCount` is read straight off `quickView.data.waitingCount` — the SAME action-required count the
 * orange quick-view card's own header states (`buildIslandQuickView`'s `isActionRequired` fold) — rather
 * than re-derived here, so the pill and the card it opens can never compute two different answers to the
 * same question. `inMotionCount` still buckets `sorted` itself: that list is already recency-windowed
 * (`sortedForTaskList`), so this reads `bucketOfTask` directly instead of re-filtering through `bucketTasks`.
 */
export function useIslandState(): IslandState {
  const quickView = useIslandQuickView();
  const working = quickView.status === 'ready'
    ? { workingCount: quickView.data.working.workingCount, workingAgents: quickView.data.working.workingAgents }
    : { workingCount: 0, workingAgents: 0 };
  const counts = quickView.status === 'ready'
    ? {
        inMotionCount: quickView.data.sorted.filter((task) => bucketOfTask(task) === 'inProgress').length,
        waitingCount: quickView.data.waitingCount,
      }
    : { inMotionCount: 0, waitingCount: 0 };
  return {
    color: resolveIslandState({ workingAgents: working.workingAgents, waitingCount: counts.waitingCount }),
    ...working,
    ...counts,
  };
}
